/**
 * Which of a set of screen-space names draw, and which yield.
 *
 * Pure geometry over boxes in the pixels of whatever pane is drawing them: no
 * camera, no scene, no DOM, no band names and no viewport of its own. The
 * Atlas's orrery is the first caller (`src/atlas/orrery.ts`); a second camera
 * that wants the same rule passes its own candidates and its own pixels.
 */

/** One name asking to be drawn, in pane pixels, y down from the top. */
export interface LabelCandidate {
  readonly id: string;
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  /** Bigger wins a collision: the drawn size of the thing being named. */
  readonly priority: number;
  /** The id this one is subordinate to, or null - a moon yields to its primary
   *  whatever their sizes. The caller derives the relationship. */
  readonly subordinateTo: string | null;
  /** The caller's own fade, 0 to 1, before anything here is applied. */
  readonly fade: number;
}

export interface LabelLayoutOptions {
  /** Clear space a box needs, as a share of its own height. */
  readonly marginShare: number;
  /** The band above that over which it comes up to full, same units. */
  readonly rampShare: number;
}

export interface LabelPlacement {
  readonly id: string;
  readonly opacity: number;
  /** Pixels of clear space to the nearest box that beat it; -1 with none. */
  readonly clearPx: number;
}

/** The pixels between two boxes: negative on either axis both ways is overlap. */
function clearance(a: LabelCandidate, b: LabelCandidate): number {
  const gapX = Math.max(a.left, b.left) - Math.min(a.right, b.right);
  const gapY = Math.max(a.top, b.top) - Math.min(a.bottom, b.bottom);
  return Math.max(gapX, gapY);
}

/**
 * Highest priority first, then greedily: a box within `marginShare` of one
 * already placed is not drawn at all, and comes up to full over `rampShare`
 * above that rather than switching on.
 *
 * The placements come back in the order the candidates were given.
 */
export function declutterLabels(
  candidates: readonly LabelCandidate[], opts: LabelLayoutOptions,
): LabelPlacement[] {
  const byId = new Map<string, LabelCandidate>();
  for (const c of candidates) byId.set(c.id, c);
  const order = candidates.map((c, i) => {
    const over = c.subordinateTo === null ? undefined : byId.get(c.subordinateTo);
    const key = over ? Math.min(c.priority, over.priority * 0.999) : c.priority;
    return { c, i, key };
  });
  order.sort((a, b) => b.key - a.key);
  const out: LabelPlacement[] = candidates.map((c) => ({ id: c.id, opacity: 0, clearPx: -1 }));
  const taken: LabelCandidate[] = [];
  for (const { c, i } of order) {
    const h = c.bottom - c.top;
    let clear = Infinity;
    for (const other of taken) clear = Math.min(clear, clearance(c, other));
    const fit = clear === Infinity
      ? 1
      : Math.min(1, Math.max(0, (clear - opts.marginShare * h) / (opts.rampShare * h)));
    const opacity = c.fade * fit;
    out[i] = { id: c.id, opacity, clearPx: clear === Infinity ? -1 : clear };
    if (opacity > 0.004) taken.push(c);
  }
  return out;
}
